import type { PricingTier } from "@/components/PricingCard";
import { signUpUrl } from "@/config/site";

/**
 * Six commercial levels, each a role in the Devory ecosystem rather than a
 * fragment of one -- see DEVORY_PRICING_ARCHITECTURE_2026-07.md for the
 * original commercial review and DEVORY_SIX_TIER_ARCHITECTURE_2026-08.md
 * (owner decision, 2026-08-17) for Studio and the approved monetary prices.
 *
 * Updated 2026-09-13: checkout is genuinely live. Real Stripe price IDs are
 * configured in production (Databases/Migrations/0040_update_v1_commercial_plan_catalogue.sql
 * in devory-cloud) and self-serve checkout works from inside Devory Cloud
 * (Settings > Billing) once a visitor signs up via signUpUrl below -- this
 * marketing site still doesn't embed a checkout form directly, so every paid
 * tier's `cta` points at sign-up, not a direct purchase link. Three
 * entitlement dimensions are genuinely server-enforced today: private
 * workspace count, organisation member-seat count, and scoped monthly cloud
 * workflow executions (input/output/constant/logger components only, not
 * arbitrary code) -- see Cloud/APIGateway/src/v1-commercial-catalogue.ts in
 * devory-cloud for the canonical numbers (V1_TIER_PRICING, EXECUTION_FEATURES)
 * and the enforcement code they map to. Export/package/deploy, broader AI
 * cloud compute, priority support and advanced roles/approval workflows are
 * still not built into any tier's real entitlement, so they stay under each
 * card's "Coming later" section -- a customer must never read this page and
 * reasonably believe they are buying functionality Devory cannot yet deliver.
 *
 * Prices are GBP, owner-approved 2026-08-17, matching
 * Cloud/APIGateway/src/v1-commercial-catalogue.ts's V1_TIER_PRICING
 * exactly (integer minor units there; whole pounds here since none of
 * these figures have a pence component). Annual is always exactly 10x
 * monthly -- "2 months free" -- never independently rounded or discounted.
 * No dollar/pound figure appears for Creator (genuinely free) or Enterprise
 * (Contact Sales, no fixed public price, not self-service).
 *
 * Tax presentation: owner-confirmed 2026-08-18, revised 2026-08-20 (Devory
 * trades as a sole-trader operation initially, not through SAMADROCK LTD --
 * see docs/LEGAL_REVIEW.md and siteConfig.legalEntity). The current legal
 * seller is not currently VAT-registered, so these prices carry no UK VAT and no tier
 * below states "+ VAT" or "VAT included" -- either would misrepresent what
 * is actually charged. This is not a permanent property; re-verify against
 * current VAT-registration status before assuming it still holds, and
 * update this comment and the customer-facing wording together if it
 * changes. Stripe Tax configuration is a separate production activation
 * concern, tracked independently.
 */
export const pricingTiers: PricingTier[] = [
  {
    id: "creator",
    name: "Creator",
    price: "Free",
    purpose: "Learn Devory and create in your own workspace.",
    promise: "Everything required to learn Devory and build with the core product, at no cost.",
    audience:
      "Students, learners, hobbyists, and anyone evaluating the platform.",
    scope: [
      "1 private workspace",
      "The full core canvas/composition experience within that workspace",
      "Create, open, save and reload projects",
      "Project version history -- browse past saves and restore any of them",
      "A real usage dashboard for your plan's entitlements",
      "Solo use (1 member)",
      "Documentation, learning resources and community support",
    ],
    notIncluded: [
      "No team collaboration -- Creator is a solo workspace",
    ],
    roadmap: [
      "Publish components to the Marketplace, including your own personal store",
      "Browse and install Marketplace components",
      "Local Devory Desktop Runtime",
    ],
    nextStep: "Move to Builder once 1 workspace isn't enough room to work in.",
    availability: "Live -- open for public self-serve sign-up today, no payment required.",
    cta: { label: "Create your account", to: signUpUrl },
  },
  {
    id: "builder",
    name: "Builder",
    price: "£18/month",
    annualPrice: "or £180/year -- 2 months free",
    purpose: "More room to work: up to 5 private workspaces.",
    promise: "Everything in Creator, with 5 private workspaces instead of 1.",
    audience: "Individual developers who've outgrown a single workspace.",
    scope: [
      "Everything in Creator",
      "5 private workspaces",
      "100 scoped cloud workflow executions / month",
    ],
    notIncluded: [
      "No team collaboration -- still solo use (1 member)",
    ],
    roadmap: [
      "Broader workflow execution beyond the current safe component set",
      "Limited export, packaging and deployment from Cloud",
      "Limited AI cloud compute",
    ],
    nextStep: "Move to Professional once 5 workspaces isn't enough room to work in.",
    availability: "Live -- self-serve checkout from Devory Cloud, billed per person.",
    cta: { label: "Create your account", to: signUpUrl },
  },
  {
    id: "professional",
    name: "Professional",
    price: "£59/month",
    annualPrice: "or £590/year -- 2 months free",
    purpose: "No workspace limit for individual work.",
    promise: "Everything in Builder, with the workspace count limit removed entirely.",
    audience: "Independent developers, freelancers and consultants working solo.",
    scope: [
      "Everything in Builder",
      "Unlimited private workspaces",
      "1,000 scoped cloud workflow executions / month",
    ],
    notIncluded: [
      "No team collaboration -- still solo use (1 member); see Studio",
    ],
    roadmap: [
      "Broader workflow execution beyond the current safe component set",
      "Unlimited export, packaging and deployment from Cloud",
      "Unlimited AI cloud compute",
      "Commercial usage rights",
      "Priority support",
    ],
    nextStep: "Move to Studio once more than one person needs shared access.",
    availability: "Live -- self-serve checkout from Devory Cloud, billed per person.",
    cta: { label: "Create your account", to: signUpUrl },
    recommended: true,
  },
  {
    id: "studio",
    name: "Studio",
    price: "£149/month",
    annualPrice: "or £1,490/year -- 2 months free",
    purpose: "Real collaboration for a small team: up to 5 members.",
    promise: "Everything in Professional, opened up to a 5-member organisation.",
    audience: "Small development teams, agencies and closely collaborating groups.",
    scope: [
      "Everything in Professional",
      "Unlimited private workspaces",
      "Up to 5 members in one organisation",
      "Real roles and permissions (Owner, Admin, Billing Admin, Member, Viewer)",
      "5,000 scoped cloud workflow executions / month, shared across the organisation",
    ],
    notIncluded: [
      "Additional seats beyond 5 are not available for purchase in V1",
    ],
    roadmap: [
      "Approval workflows for sensitive membership/role changes",
      "Pooled shared cloud compute beyond the per-organisation execution allowance",
      "Purchasable additional seats beyond 5",
    ],
    nextStep: "Move to Team once 5 members isn't enough room for your organisation.",
    availability: "Live -- self-serve checkout from Devory Cloud, billed per organisation.",
    cta: { label: "Create your account", to: signUpUrl },
  },
  {
    id: "team",
    name: "Team",
    price: "£349/month",
    annualPrice: "or £3,490/year -- 2 months free",
    purpose: "An established organisation: up to 10 members.",
    promise: "Everything in Studio, with room for a larger organisation.",
    audience: "Growing development teams, studios and agencies.",
    scope: [
      "Everything in Studio",
      "Unlimited private workspaces",
      "Up to 10 members in one organisation",
      "20,000 scoped cloud workflow executions / month, shared across the organisation",
    ],
    notIncluded: [
      "Additional seats beyond 10 are not available for purchase in V1",
    ],
    roadmap: [
      "Multiple Marketplace stores per organisation",
      "Approval workflows for sensitive membership/role changes",
      "Pooled shared cloud compute beyond the per-organisation execution allowance",
      "Purchasable additional seats beyond 10",
    ],
    nextStep: "Move to Enterprise for organisation-wide governance, SSO and compliance needs.",
    availability: "Live -- self-serve checkout from Devory Cloud, billed per organisation.",
    cta: { label: "Create your account", to: signUpUrl },
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Contact Sales",
    purpose: "Custom platform deployments.",
    promise: "A direct conversation about what your organisation actually needs.",
    audience: "Large organisations, governments, universities and regulated industries.",
    scope: [
      "Everything in Team",
      "Unlimited private workspaces",
      "A member allowance set individually per contract, not a fixed self-serve number",
    ],
    roadmap: [
      "Enterprise governance and organisation-wide policy",
      "Single sign-on (SSO) and SCIM provisioning",
      "Advanced security controls, compliance and audit support",
      "Dedicated infrastructure options",
      "Enterprise support and a named account manager",
    ],
    availability:
      "Enterprise engagements are scoped individually through a direct conversation with Devory. Not purchasable through self-service checkout.",
    cta: { label: "Talk to Devory", to: "/contact" },
  },
];
